import { getSnapshot } from 'mobx-state-tree';
import React, { useEffect, useState } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';
import { FormattedNumber } from 'react-intl';
import { useHistory } from 'react-router-dom';
import Box from 'ui-box';
import {
  Button,
  Card,
  Container,
  Grid,
  Heading,
  Icon,
  majorScale,
  PageContainer,
  Spinner,
  useToast,
} from '../../../npaper';
import { flagEmojiFromCode } from '../../../npaper/CountrySelect';
import { IVatRate, useStores } from '../../../stores';
import { VatRateDialog } from './VatRateDialog';

export const VatRatesPage = () => {
  const history = useHistory();
  const toaster = useToast();

  const { vatRateStore } = useStores();

  const [rates, setRates] = useState<IVatRate[]>();

  const [editVatRate, setEditVatRate] = useState<IVatRate>();
  const [addVatRate, setAddVatRate] = useState(false);

  useEffect(() => {
    let isSub = true;
    vatRateStore
      .getAll()
      .then(({ items: rates }) => isSub && setRates(rates))
      .catch(toaster.showDefaultError);
    return () => {
      isSub = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reorder = (result: DropResult) => {
    if (!rates) return;
    const rate = rates[result.source.index];
    if (
      result.destination &&
      result.destination.index !== result.source.index
    ) {
      const destRate = rates[result.destination.index];
      // Swap out the rates
      vatRateStore.updateItem(rate, {
        ...getSnapshot(rate),
        position: destRate.position,
      });

      setRates((rates) => {
        const copy = [...(rates || [])];
        const removed = copy.splice(result.source.index, 1);
        copy.splice(result.destination!.index, 0, ...removed);
        return copy;
      });
    }
  };

  return (
    <PageContainer>
      <VatRateDialog
        vatRate={editVatRate}
        isShown={!!editVatRate}
        onClose={() => setEditVatRate(undefined)}
      />
      <VatRateDialog
        isShown={addVatRate}
        onClose={() => setAddVatRate(false)}
      />
      <Container>
        <Button
          iconLeft="arrow-left"
          marginBottom={majorScale(2)}
          onClick={() =>
            history.length > 0 ? history.goBack() : history.push('/settings')
          }
        >
          Indstillinger
        </Button>

        <Grid>
          <Card className="col-span-12 md:col-span-12 lg:col-span-10 xl:col-span-8">
            <div className="dialog">
              <Box className="header">
                <Heading size={400}>Momskoder</Heading>

                <Button iconLeft="add" onClick={() => setAddVatRate(true)}>
                  Create VAT rate
                </Button>
              </Box>
              <Grid padding={majorScale(2)}>
                <div className="col-span-6 col-start-4">
                  {!rates ? (
                    <Spinner />
                  ) : (
                    <DragDropContext
                      onDragEnd={(result, provided) => {
                        reorder(result);
                      }}
                    >
                      <Droppable droppableId="vat-rates">
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                          >
                            {rates.map((rate, i) => (
                              <Draggable
                                draggableId={`rate-${rate.id}`}
                                key={rate.id}
                                index={i}
                              >
                                {(provided, snapshot) => (
                                  <Card
                                    backgroundColor="white"
                                    padding={majorScale(1)}
                                    display="flex"
                                    alignItems="center"
                                    marginTop={majorScale(1)}
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                  >
                                    <div
                                      className="pl-2 pr-4"
                                      {...provided.dragHandleProps}
                                    >
                                      <Icon icon="drag-handle-horizontal" />
                                    </div>
                                    <Box>
                                      <p>
                                        {rate.code}{' '}
                                        <span className="muted p small">
                                          {rate.display_name}
                                        </span>
                                      </p>
                                      <p className="small muted">{rate.name}</p>
                                      {rate.countries.length > 0 && (
                                        <p className="small muted">
                                          Andvendes i{' '}
                                          {rate.countries.map((code) =>
                                            flagEmojiFromCode(code)
                                          )}
                                        </p>
                                      )}
                                      {rate.exclude_countries.length > 0 && (
                                        <p className="small muted">
                                          Andvendes ikke i{' '}
                                          {rate.exclude_countries.map((code) =>
                                            flagEmojiFromCode(code)
                                          )}
                                        </p>
                                      )}
                                    </Box>
                                    <Box
                                      is="p"
                                      marginLeft="auto"
                                      className="font-mono"
                                      paddingRight={majorScale(1)}
                                    >
                                      <FormattedNumber
                                        /* eslint-disable-next-line react/style-prop-object */
                                        style="percent"
                                        value={rate.rate}
                                      />
                                    </Box>
                                    <Button
                                      iconLeft="edit"
                                      onClick={() => setEditVatRate(rate)}
                                      minimal
                                    />
                                  </Card>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  )}
                </div>
              </Grid>
            </div>
          </Card>
        </Grid>
      </Container>
    </PageContainer>
  );
};
